import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { getCourseInitials } from '../../course-list.utils';

export type CourseCardMode = 'learner' | 'instructor';
export type LearnerStatus = 'enrolled' | 'in-progress' | 'completed' | 'overdue' | 'enrollment-expired';

@Component({
  selector: 'app-course-card',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  templateUrl: './course-card.component.html'
})
export class CourseCardComponent {
  @Input() mode: CourseCardMode = 'learner';
  @Input() courseId = '';
  @Input() title = '';
  @Input() description = '';
  @Input() thumbnail: string | null = null;
  @Input() organizationName = '';

  // Instructor-specific
  @Input() status: 'draft' | 'published' | 'archived' = 'draft';
  @Input() modulesCount = 0;
  @Input() createdAt = '';

  // Learner-specific
  @Input() learnerStatus: LearnerStatus = 'enrolled';
  @Input() progressPercent = 0;
  @Input() hasCertificate = false;
  @Input() canContinue = false;
  @Input() enrollmentDeadline = '';
  @Input() unitsCount = 0;
  @Input() quizzesCount = 0;
  // KAN-353: purchased access window lapsed (paid course, progress retained).
  // Deliberately NOT a LearnerStatus - 'enrollment-expired' is the enrollment
  // DEADLINE concept; this one has a purchase path (Renew access CTA).
  @Input() accessExpired = false;

  @Output() cardClick = new EventEmitter<string>();
  @Output() continueClick = new EventEmitter<string>();
  @Output() renewClick = new EventEmitter<string>();

  // KAN-427: the learner progress block renders only for in-progress cards.
  // It carries the single flexible spacer (mt-auto) that bottom-anchors the
  // progress->button->footer group so bars align across a grid row; the footer's
  // own mt-auto is therefore suppressed in this state (two spacers would split
  // the free space and only half-align the bars).
  get showProgressBar(): boolean {
    return this.mode === 'learner' && this.learnerStatus === 'in-progress';
  }

  get routerLinkPath(): any[] {
    return this.mode === 'instructor'
      ? ['/instructor/courses', this.courseId, 'manage']
      : ['/courses', this.courseId];
  }

  get initials(): string {
    return getCourseInitials(this.title || 'Course');
  }

  get statusBadgeClass(): string {
    if (this.mode === 'instructor') {
      return this.status === 'published' ? 'badge-success' :
             this.status === 'archived' ? 'badge-error' : 'badge-warning';
    }
    if (this.accessExpired) return 'badge-error';
    switch (this.learnerStatus) {
      case 'completed': return 'badge-success';
      case 'in-progress': return 'badge-primary';
      case 'overdue': return 'badge-error';
      case 'enrollment-expired': return 'badge-ghost';
      default: return 'badge-warning';
    }
  }

  get statusLabel(): string {
    if (this.mode === 'instructor') {
      return (this.status || 'draft').charAt(0).toUpperCase() + (this.status || 'draft').slice(1);
    }
    if (this.accessExpired) return 'Access Expired';
    switch (this.learnerStatus) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'overdue': return 'Overdue';
      case 'enrollment-expired': return 'Enrollment Expired';
      default: return 'Enrolled';
    }
  }

  get timeAgo(): string {
    if (!this.createdAt) return '';
    const diff = Date.now() - new Date(this.createdAt).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1mo ago' : `${months}mo ago`;
  }

  onCardClick(): void {
    this.cardClick.emit(this.courseId);
  }

  onContinue(event: Event): void {
    event.stopPropagation();
    this.continueClick.emit(this.courseId);
  }

  onRenew(event: Event): void {
    event.stopPropagation();
    this.renewClick.emit(this.courseId);
  }
}
